import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from './TanStackTable';
import Chip from './Chip';
import { useGetCampaignImpact, useUploadPlantationCertificate } from '../queries/campaign';
import MediaUpload from './Forms/MediaUpload';
import { toast } from 'react-toastify';

const CampaignDetailsModal = ({ isOpen, onClose, campaign }) => {
    const columnHelper = createColumnHelper();

    // Only make API call when modal is open and we have a campaign ID
    const { data: impactDataResponse = {}, isPending, isError, error } = useGetCampaignImpact(
        isOpen && campaign?._id ? campaign._id : null
    );

    // Columns for Plant Trees participants (volunteers)
    // Certificate upload modal state
    const [certificateModal, setCertificateModal] = React.useState({ open: false, user: null });
    const [certificateFile, setCertificateFile] = React.useState(null);
    const { mutate: uploadCertificate, isLoading: isUploading } = useUploadPlantationCertificate();

    if (!isOpen) return null;

    // Debug: Log the API response to understand the data structure
    console.log('Campaign Impact API Response:', impactDataResponse);
    console.log('Campaign Data:', campaign);

    // Determine campaign type
    const campaignType = impactDataResponse?.campaignType || campaign?.promotionType || campaign?.type;
    const isPromoteBrandCampaign = campaignType === 'Promote Brand/Product';
    const isSponsorCampaign = campaignType === 'Sponsor a Challenge' || campaignType === 'Sponsor';
    const isAdvertisementCampaign = campaignType === 'Advertisement';
    const isTreePlantationCampaign = campaignType === 'Plant Trees';
    // Sponsor and Promote Brand/Product share the same impact layout
    const isCouponBasedCampaign = isPromoteBrandCampaign || isSponsorCampaign;

    // Use data from API response if available, otherwise fall back to campaign data
    const impactData = isCouponBasedCampaign ? {
        // For Promote Brand/Product and Sponsor campaigns (identical layout)
        totalCarbonSavingsKg: impactDataResponse?.totalCarbonSavingsKg ?? 0,
        totalPointsRedeemed: impactDataResponse?.totalPointsRedeemed || 0,
        totalCouponsAvailable: impactDataResponse?.totalCouponsAvailable || 0,
        totalCouponsAssigned: impactDataResponse?.totalCouponsAssigned || 0,
        totalCouponsRedeemed: impactDataResponse?.totalCouponsRedeemed || 0,
        couponRedemptionRate: impactDataResponse?.couponRedemptionRate || 0,
        // Product click metrics
        totalProductClicks: impactDataResponse?.totalProductClicks || 0,
        uniqueProductClicks: impactDataResponse?.uniqueProductClicks || 0,
        couponsByStatus: impactDataResponse?.couponsByStatus || {
            assigned: 0,
            redeemed: 0,
            unassigned: 0
        }
    } : isAdvertisementCampaign ? {
        // For Advertisement campaigns
        totalImpressions: impactDataResponse?.totalImpressions || 0,
        uniqueImpressions: impactDataResponse?.uniqueImpressions || 0,
        totalClicks: impactDataResponse?.totalClicks || 0,
        uniqueClicks: impactDataResponse?.uniqueClicks || 0,
        clickThroughRate: impactDataResponse?.clickThroughRate || 0,
        uniqueClickThroughRate: impactDataResponse?.uniqueClickThroughRate || 0
    } : isTreePlantationCampaign ? {
        // For Plant Trees campaigns
        totalTreesPlanted: impactDataResponse?.totalTreesPlanted || campaign?.totalTreesPlanted || 0,
        totalVolunteers: impactDataResponse?.totalVolunteers || 0,
        totalCertificatesIssued: impactDataResponse?.totalCertificateIssued || impactDataResponse?.totalCertificatesIssued || campaign?.totalCertificatesIssued || 0,
        totalPointsDonated: impactDataResponse?.totalPointsDonated || 0,
        volunteers: impactDataResponse?.volunteers || campaign?.volunteers || [],
        uniqueAdImpressions: impactDataResponse?.ctr?.uniqueAdImpressions || 0,
        totalAdImpressions: impactDataResponse?.ctr?.totalAdImpressions || 0,
        totalAdClicks: impactDataResponse?.ctr?.totalAdClicks || 0,
        uniqueAdClicks: impactDataResponse?.ctr?.uniqueAdClicks || 0,
        totalCTR: impactDataResponse?.ctr?.totalCTR || 0,
        actualCTR: impactDataResponse?.ctr?.actualCTR || 0,
        averageCTR: impactDataResponse?.ctr?.averageCTR || 0,
        totalCarbonSavingsKg: impactDataResponse?.totalCarbonSavingsKg ?? 0,
    } : {
        // Fallback for any other campaign type
        carbonSaving: impactDataResponse?.totalCarbonSavingsKg || campaign?.impactData?.carbonSaving || 0,
        totalChallengePoints: impactDataResponse?.totalChallengePoints || campaign?.impactData?.totalChallengePoints || 0,
        carbonPoints: impactDataResponse?.totalAllocatedCarbonPoints || campaign?.impactData?.carbonPoints || 0
    };

    // Submissions data - use different data structure based on campaign type
    const submissions = isCouponBasedCampaign ?
        (impactDataResponse?.couponActivity || impactDataResponse?.submissionsWithCoupons || []) :
        isAdvertisementCampaign ?
            (impactDataResponse?.impressionDetails || []) :
            isTreePlantationCampaign ?
                (impactDataResponse?.volunteers || impactData.volunteers || []) :
                (impactDataResponse?.submissionsWithCoupons || campaign?.participants || []);

    // Click details for Advertisement campaigns
    const clickDetails = impactDataResponse?.clickDetails || [];

    // Coupon trails data for Promote Brand/Product campaigns
    const couponTrails = impactDataResponse?.couponTrails || [];

    // Additional metrics from API
    const additionalMetrics = {
        totalSubmissions: impactDataResponse?.totalSubmissions || 0,
        submissionsCount: submissions?.length || 0
    };

    // Updated submissions columns based on API response structure
    const submissionsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('userId.username', {
            header: 'Username',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('reviewStatus', {
            header: 'Status',
            align: 'center',
            cell: info => {
                const isSubmitted = info.row.original.isSubmitted;
                const reviewStatus = info.row.original.reviewStatus;
                const allocatedPoints = info.row.original.allocatedPoints;
                const getStatusColor = (isSubmitted, status, points) => {
                    if (!isSubmitted) {
                        return `yellow`;
                    } else if (isSubmitted && status === 'pending') {
                        return `orange`;
                    } else if (isSubmitted && status === 'submitted' && points > 0) {
                        return `green`;
                    } else if (isSubmitted && status === 'rejected') {
                        return `inactive`;
                    }
                };
                console.log(info.cell.row.original);
                const getSubmissionStatus = (isSubmitted, status, points) => {
                    if (!isSubmitted) {
                        return `Enrolled`;
                    } else if (isSubmitted && status === 'pending') {
                        return `Submitted`;
                    } else if (isSubmitted && status === 'submitted' && points > 0) {
                        return `Accepted`;
                    } else if (isSubmitted && status === 'rejected') {
                        return `Rejected`;
                    }
                    return `Not Submitted - ${points} Points`;
                }
                return <Chip value={getSubmissionStatus(isSubmitted, reviewStatus, allocatedPoints)} color={getStatusColor(isSubmitted, reviewStatus, allocatedPoints)} />;
            },
        }),
        columnHelper.accessor('carbonEmissionCount', {
            header: 'Carbon Saving',
            align: 'center',
            cell: info => {
                const value = info.getValue();
                return value !== null && value !== undefined ? `${value} KG` : 'N/A';
            },
        }),
        columnHelper.accessor('totalPoints', {
            header: 'Total Challenge Points',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('actualCarbonSavingPoints', {
            header: 'Carbon Points',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('userCoupon', {
            header: 'Coupon Status',
            align: 'center',
            cell: info => {
                const userCoupon = info.getValue();
                const submission = info.row.original;

                const getCouponStatusColor = (status) => {
                    switch (status) {
                        case 'assigned': return 'blue';
                        case 'redeemed': return 'green';
                        case 'expired': return 'red';
                        default: return 'gray';
                    }
                };

                if (userCoupon && userCoupon.status === 'assigned') {
                    return (
                        <div className="text-center">
                            <div className="text-sm text-secondary mb-1">{userCoupon.couponId?.code || 'Coupon Code'}</div>
                            <Chip value="Assigned" color={getCouponStatusColor('assigned')} />
                            <div className="text-sm text-secondary mt-1">{new Date(userCoupon.createdAt).toRegularDateTime()}</div>
                        </div>
                    );
                } else if (userCoupon && userCoupon.status === 'redeemed') {
                    const redeemedDate = userCoupon.redeemedAt ? new Date(userCoupon.redeemedAt).toRegularDateTime() : 'N/A';

                    return (
                        <div className="text-center">
                            <div className="text-sm text-secondary mb-1">{userCoupon.couponId?.code || 'Coupon Code'}</div>
                            <Chip value="Redeemed" color={getCouponStatusColor('redeemed')} />
                            <div className="text-sm text-secondary mt-1">
                                {redeemedDate}
                            </div>
                        </div>
                    );
                } else {
                    return <Chip value="N/A" color="gray" />;
                }
            },
        }),
    ];

    // Columns for Promote Brand/Product campaign type (coupon activity)
    const couponActivityColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('user.username', {
            header: 'Username',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('coupon.code', {
            header: 'Coupon Code',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            align: 'center',
            cell: info => {
                const status = info.getValue();
                const getCouponStatusColor = (status) => {
                    switch (status) {
                        case 'assigned': return 'blue';
                        case 'redeemed': return 'green';
                        case 'expired': return 'red';
                        default: return 'gray';
                    }
                };
                return <Chip value={status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'} color={getCouponStatusColor(status)} />;
            },
        }),
        columnHelper.accessor('pointsRedeemed', {
            header: 'Points Redeemed',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('assignedAt', {
            header: 'Assigned Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                return date ? new Date(date).toLocaleDateString() : 'N/A';
            },
        }),
        columnHelper.accessor('redeemedAt', {
            header: 'Redeemed Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                return date ? new Date(date).toLocaleDateString() : 'N/A';
            },
        }),
    ];

    // Columns for coupon trails (Promote Brand/Product campaigns)
    const couponTrailsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('code', {
            header: 'Coupon Code',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('product.name', {
            header: 'Product',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('discountValue', {
            header: 'Discount',
            align: 'center',
            cell: info => {
                const value = info.getValue();
                const type = info.row.original.typeOfDiscount;
                if (type === 'percentage') {
                    return `${value}%`;
                } else {
                    return `${value} ₹`;
                }
            },
        }),
        columnHelper.accessor('pointsRequired', {
            header: 'Points Required',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('total_redeems_count', {
            header: 'Total Redeems',
            align: 'center',
            cell: info => `${info.getValue() || 0}/${info.row.original.maxRedemptionCount || 0}`,
        }),
        columnHelper.accessor('active', {
            header: 'Status',
            align: 'center',
            cell: info => {
                const isActive = info.getValue();
                return <Chip value={isActive ? 'Active' : 'Inactive'} color={isActive ? 'green' : 'red'} />;
            },
        }),
    ];

    // Columns for Advertisement impression details
    const impressionDetailsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('userId', {
            header: 'User ID',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('location', {
            header: 'Location',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('deviceType', {
            header: 'Device Type',
            align: 'center',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('totalImpressions', {
            header: 'Total Impressions',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('revisitCount', {
            header: 'Revisit Count',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor('impressionAt', {
            header: 'Impression Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                return date ? new Date(date).toLocaleDateString() : 'N/A';
            },
        }),
    ];

    const treeParticipantsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor(row => row.username || row.userId?.username || row.user?.username, {
            id: 'username',
            header: 'Username',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor(row => row.totalTreesPlanted || row.treesPlanted || row.totalPlanted || 0, {
            id: 'treesPlanted',
            header: 'Total Trees Planted',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor(row => row.totalPointsDonated || row.pointsDonated || row.donatedPoints || 0, {
            id: 'pointsDonated',
            header: 'Total Points Donated',
            align: 'center',
            cell: info => info.getValue() || 0,
        }),
        columnHelper.accessor(row => row.certificateIssued || row.certificate || row.certificatesCount || false, {
            id: 'certificateIssued',
            header: 'Certificate Issued',
            align: 'center',
            cell: info => {
                const val = info.getValue();
                const issued = !!val && (typeof val === 'boolean' ? val : (Number(val) > 0));
                return <span>{issued ? 'Yes' : 'No'}</span>;
            }
        }),
        // columnHelper.display({
        //     id: 'actions',
        //     header: 'Actions',
        //     cell: info => {
        //         const row = info.row.original;
        //         const userId = row.userId?._id || row.user?._id || row._id || row.id;
        //         return (
        //             <button
        //                 className="px-2 py-1 bg-primary text-white rounded text-xs"
        //                 onClick={() => setCertificateModal({ open: true, user: { userId, username: row.username || row.userId?.username || row.user?.username } })}
        //             >
        //                 Upload Certificate
        //             </button>
        //         );
        //     }
        // })
    ];

    // Columns for Advertisement click details
    const clickDetailsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('userId', {
            header: 'User ID',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('location', {
            header: 'Location',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('deviceType', {
            header: 'Device Type',
            align: 'center',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('clickedAt', {
            header: 'Click Date',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                return date ? new Date(date).toLocaleDateString() : 'N/A';
            },
        }),
    ];

    if (isPending) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-center p-8">
                        <div className="text-lg">Loading campaign details...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex flex-col items-center justify-center p-8">
                        <div className="text-lg text-red-600 mb-4">Error loading campaign details</div>
                        <div className="text-sm text-secondary mb-4">{error?.message || 'An unexpected error occurred'}</div>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-xl font-bold text-gray-900">
                        Campaign Details: {impactDataResponse?.campaignTitle || campaign?.title || campaign?.campaignTitle || 'N/A'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        title="Close"
                    >
                        <FaTimes className="text-secondary" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Impact Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {isCouponBasedCampaign ? 'Coupon Performance Summary' :
                                isAdvertisementCampaign ? 'Advertisement Performance Summary' : 'Impact Summary'}
                        </h3>

                        {/* Campaign Type Display */}
                        <div className="mb-4">
                            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                Campaign Type: {campaignType || 'N/A'}
                            </div>
                        </div>

                        <div className={`grid grid-cols-1 ${isCouponBasedCampaign ? 'md:grid-cols-6' : 'md:grid-cols-4'} gap-4`}>
                            {isCouponBasedCampaign ? (
                                // Promote Brand/Product & Sponsor campaign metrics (identical layout)
                                <>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-green-600">
                                            {impactData.totalCarbonSavingsKg} Kg
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Carbon Savings</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-primary">
                                            {impactData.totalPointsRedeemed}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Points Redeemed</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipBlue">
                                            {impactData.totalCouponsRedeemed}/{impactData.totalCouponsAvailable}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Coupons Redeemed/Available</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipPurple">
                                            {impactData.couponRedemptionRate}%
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Redemption Rate</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-orange">
                                            {impactData.totalProductClicks}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Product Clicks</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipYellow">
                                            {impactData.uniqueProductClicks}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Unique Product Clicks</div>
                                    </div>
                                </>
                            ) : isAdvertisementCampaign ? (
                                // Advertisement campaign metrics
                                <>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-primary">
                                            {impactData.totalImpressions}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Impressions</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipBlue">
                                            {impactData.uniqueImpressions}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Unique Impressions</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipPurple">
                                            {impactData.totalClicks}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Clicks</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipYellow">
                                            {impactData.uniqueClicks}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Unique Clicks</div>
                                    </div>
                                </>
                            ) : isTreePlantationCampaign ? (
                                // Plant Trees metrics
                                <div className='w-full grid col-span-4 gap-2'>
                                    <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipYellow">
                                                {impactData.totalCarbonSavingsKg} Kg
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Carbon Savings</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-primary">
                                                {impactData.totalTreesPlanted}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Trees Planted</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipBlue">
                                                {impactData.totalVolunteers}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Volunteers</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipPurple">
                                                {impactData.totalCertificatesIssued}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Certificates Issued</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-orange">
                                                {impactData.totalPointsDonated}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Points Donated</div>
                                        </div>
                                    </div>
                                    <div className='grid grid-cols-1 md:grid-cols-7 gap-2'>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-primary">
                                                {impactData.uniqueAdImpressions}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Unique Ad Impressions</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipBlue">
                                                {impactData.totalAdImpressions}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Ad Impressions</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-orange">
                                                {impactData.uniqueAdClicks}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Unique Ad Clicks</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipPurple">
                                                {impactData.totalAdClicks}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Ad Clicks</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipMagenta">
                                                {impactData.totalCTR}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Total Click-Through Rate</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipYellow">
                                                {impactData.actualCTR}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Actual Click-Through Rate</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 text-center shadow">
                                            <div className="text-2xl font-bold text-chipRed">
                                                {impactData.averageCTR}
                                            </div>
                                            <div className="text-sm text-secondary mt-1">Average Click-Through Rate</div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Sponsor a Challenge campaign metrics
                                <>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-primary">
                                            {impactData.carbonSaving} KG
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Carbon Saving</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipBlue">
                                            {impactData.totalChallengePoints}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Total Challenge Points</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow">
                                        <div className="text-2xl font-bold text-chipPurple">
                                            {impactData.carbonPoints}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Carbon Points</div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Additional metrics for coupon-based campaigns (Promote Brand/Product & Sponsor) */}
                        {isCouponBasedCampaign && impactData.couponsByStatus && (
                            <div className="mt-6">
                                <h4 className="text-md font-semibold text-gray-800 mb-3">Coupon Status Breakdown</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-primary">
                                        <div className="text-2xl font-bold text-primary">
                                            {impactData.couponsByStatus.redeemed}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Redeemed</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-chipBlue">
                                        <div className="text-2xl font-bold text-chipBlue">
                                            {impactData.couponsByStatus.assigned}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Assigned</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-chipYellow">
                                        <div className="text-2xl font-bold text-chipYellow">
                                            {Math.abs(impactData.couponsByStatus.unassigned)}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Yet to Redeem</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tables Section - Different based on campaign type */}
                    {isCouponBasedCampaign ? (
                        // Promote Brand/Product & Sponsor campaign tables
                        <>
                            {/* Coupon Activity Table */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Participant List</h3>
                                <div className="bg-white rounded-lg border border-border">
                                    {submissions && submissions.length > 0 ? (
                                        <TanStackTable
                                            data={submissions}
                                            columns={couponActivityColumns}
                                            enablePagination={true}
                                        />
                                    ) : (
                                        <div className="p-8 text-center text-secondary">
                                            No coupon activity data available
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Coupon Trails Table */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Coupon Details</h3>
                                <div className="bg-white rounded-lg border border-border">
                                    {couponTrails && couponTrails.length > 0 ? (
                                        <TanStackTable
                                            data={couponTrails}
                                            columns={couponTrailsColumns}
                                            enablePagination={true}
                                        />
                                    ) : (
                                        <div className="p-8 text-center text-secondary">
                                            No coupon details available
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : isTreePlantationCampaign ? (
                        // Plant Trees participants table
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Participant List</h3>
                            <div className="bg-white rounded-lg border border-border">
                                {submissions && submissions.length > 0 ? (
                                    <TanStackTable
                                        data={submissions}
                                        columns={treeParticipantsColumns}
                                        enablePagination={true}
                                    />
                                ) : (
                                    <div className="p-8 text-center text-secondary">
                                        No volunteer data available
                                    </div>
                                )}
                            </div>
                            {/* Certificate Upload Modal */}
                            {certificateModal.open && (() => {
                                const { register, handleSubmit, reset, formState: { errors } } = require('react-hook-form').useForm();
                                const onSubmit = data => {
                                    const file = data.certificate?.[0];
                                    if (!file || !certificateModal.user?.userId) {
                                        toast.error("Please select a file");
                                        return;
                                    }
                                    uploadCertificate({
                                        userId: certificateModal.user.userId,
                                        campaignId: campaign?._id,
                                        certificate: file
                                    }, {
                                        onSuccess: () => {
                                            setCertificateModal({ open: false, user: null });
                                            setCertificateFile(null);
                                            reset();
                                            // Optionally show a toast or reload data
                                        }
                                    });
                                };
                                return (
                                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                                        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                                            <button
                                                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                                                onClick={() => { setCertificateModal({ open: false, user: null }); setCertificateFile(null); reset(); }}
                                            >
                                                <FaTimes />
                                            </button>
                                            <h4 className="text-lg font-semibold mb-4">Upload Certificate for {certificateModal.user?.username}</h4>
                                            <form onSubmit={handleSubmit(onSubmit)}>
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium mb-2">Upload Certificate</label>
                                                    <input
                                                        type="file"
                                                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                                                        className="block w-full border border-gray-300 rounded p-2"
                                                        {...register('certificate', {
                                                            required: 'Certificate file is required',
                                                            validate: files => files && files.length === 1
                                                        })}
                                                    />
                                                    {errors.certificate && (
                                                        <div className="mt-2 text-xs text-red-600">
                                                            {errors.certificate.message}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    type="submit"
                                                    className="w-full py-2 bg-primary text-white rounded font-semibold disabled:opacity-60"
                                                    disabled={isUploading}
                                                >
                                                    {isUploading ? 'Uploading...' : 'Submit'}
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : !isAdvertisementCampaign ? (
                        // Sponsor a Challenge campaign table (excluding Advertisement campaigns)
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Participant List</h3>
                            <div className="bg-white rounded-lg border border-border">
                                {submissions && submissions.length > 0 ? (
                                    <TanStackTable
                                        data={submissions}
                                        columns={submissionsColumns}
                                        enablePagination={true}
                                    />
                                ) : (
                                    <div className="p-8 text-center text-secondary">
                                        No submissions data available
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetailsModal;